import React from 'react'

import { ChipSwitch, ChipSwitchButton } from '../../components/ChipSwitch'
import { useFinancialsTableContext } from '../../components/financials-table'
import { EarningsDataEnum, EarningsTimeframeEnum } from '../earnings-common/types'
import { EarningsSettings } from './types'

interface Props {
  earningsTimeframe: EarningsTimeframeEnum
  isSemiAnnualEarning: boolean
  showTimeframe: boolean
}

export default function WidgetSettingsSection({ earningsTimeframe, isSemiAnnualEarning, showTimeframe }: Props) {
  const { settings, setSettings } = useFinancialsTableContext<EarningsSettings>()

  const toggleSetting = (key: keyof EarningsSettings, value: EarningsSettings[keyof EarningsSettings]) => {
    setSettings({
      ...settings,
      [key]: value,
    })
  }

  const yoyGrowthLabel = React.useMemo(() => {
    const isQuarterlyTimeframe = earningsTimeframe === EarningsTimeframeEnum.quarterly // EarningsTimeframeEnum.quarterly is used for both quarterly & semiannual

    if (isQuarterlyTimeframe) {
      return isSemiAnnualEarning ? 'SoS Growth' : 'QoQ Growth'
    }

    return 'YoY Growth'
  }, [isSemiAnnualEarning, earningsTimeframe])

  return (
    <div className="flex flex-row space-x-5">
      <ChipSwitch size="small" label="Data">
        <ChipSwitchButton
          active={settings[EarningsDataEnum.yoyChange]}
          onClick={() => {
            toggleSetting(EarningsDataEnum.yoyChange, !settings[EarningsDataEnum.yoyChange])
          }}
        >
          {yoyGrowthLabel}
        </ChipSwitchButton>
        <ChipSwitchButton
          active={settings[EarningsDataEnum.yoyChangePct]}
          onClick={() => {
            toggleSetting(EarningsDataEnum.yoyChangePct, !settings[EarningsDataEnum.yoyChangePct])
          }}
        >
          {`${yoyGrowthLabel} %`}
        </ChipSwitchButton>
      </ChipSwitch>

      {showTimeframe && (
        <ChipSwitch size="small" label="Timeframe">
          <ChipSwitchButton
            active={earningsTimeframe === EarningsTimeframeEnum.annual}
            onClick={() => {
              toggleSetting('earningsTimeframe', EarningsTimeframeEnum.annual)
            }}
          >
            Annual
          </ChipSwitchButton>
          <ChipSwitchButton
            active={earningsTimeframe === EarningsTimeframeEnum.quarterly}
            onClick={() => {
              toggleSetting('earningsTimeframe', EarningsTimeframeEnum.quarterly)
            }}
          >
            {isSemiAnnualEarning ? 'Semiannual' : 'Quarterly'}
          </ChipSwitchButton>
        </ChipSwitch>
      )}
    </div>
  )
}
